import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TasksService } from '../../shared/services/tasks/tasks.service';
import { constants } from '../../shared/utils/constants';
import { LoggerService } from '../../shared/services/logger.service';
import { OnlineTaskInspectionDetail } from './online-task-inspection-detail.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { taskDataQuery } from './online-task-inspection-detail.data';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-photos',
  templateUrl: './online-task-inspection-detail.component.html',
  styleUrls: ['./online-task-inspection-detail.component.scss'],
})
export class OnlineTaskInspectionDetailComponent implements OnInit, OnDestroy {
  @Input() task: OnlineTaskInspectionDetail;
  private alertsContainerElement?: ElementRef<HTMLElement>;
  private alertsMutationObserver: MutationObserver;
  private routeSubscription: Subscription;
  private taskSubscription: Subscription;
  public isServerError = false;
  public constants = constants;
  public cantSendAlert: boolean = false;
  public isSubmittingOnlineInspection: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private logger: LoggerService,
    private tasksService: TasksService,
  ) {
  }

  @ViewChild('alertsContainer')
  set alertsContainer(elementRef: ElementRef<HTMLElement> | undefined) {
    this.alertsContainerElement = elementRef;
    if (this.alertsContainerElement) {
    this.initAlertsMutationObserver();
    } else if (this.alertsMutationObserver) {
      this.alertsMutationObserver.disconnect();
    }
  }

  showSentAttachmentButton(): boolean {
    if (!this.task) {
      return false;
    }
    return (
      this.task.tokenType ===
      this.constants.TOKEN_TYPE_CLIENT_ONLINE_TASK_INSPECTION
    ) &&
    this.canUploadFiles();
  }

  public canUploadFiles(): boolean {
    if (this.task.tokenType === this.constants.TOKEN_TYPE_CLIENT_SMS_UPLOAD) {
      return true;
    }
    if (this.task.tokenType === this.constants.TOKEN_TYPE_CLIENT_ONLINE_TASK_INSPECTION && this.task.state.name === constants.TASK_STATE_INFORMATION_GATHERING) {
      return true;
    }
    return false;
  }

  getTask(tokenId: string): void {
    const taskObs = this.tasksService.getTaskByTokenIdAndWatch(
      tokenId.toString(),
      taskDataQuery
    );
    if (this.taskSubscription) {
      this.taskSubscription.unsubscribe();
    }
    this.taskSubscription = taskObs.subscribe(
      (taskDetail: OnlineTaskInspectionDetail) => {
        this.task = taskDetail;
      },
      (error) => {
        this.isServerError = true;
        this.router.navigate(['/online-task-inspection/find-task']);
      }
    );
  }

  ngOnInit(): void {
    this.routeSubscription = this.route.params.subscribe((data) => {
      this.getTask(data.token);
    });
  }

  ngOnDestroy(): void {
    if (this.alertsMutationObserver) {
      this.alertsMutationObserver.disconnect();
    }

    this.routeSubscription.unsubscribe();
    if (this.taskSubscription) {
      this.taskSubscription.unsubscribe();
    }
  }

  private initAlertsMutationObserver(): void {
    if (!this.alertsContainerElement) {
      return;
    }

    if (this.alertsMutationObserver) {
      this.alertsMutationObserver.disconnect();
    }

    this.alertsMutationObserver = new MutationObserver(() => {
      this.scrollToAlertsContainer();
    });

    this.alertsMutationObserver.observe(this.alertsContainerElement.nativeElement, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  private scrollToAlertsContainer(): void {
    if (!this.alertsContainerElement) {
      return;
    }

    const container = this.alertsContainerElement.nativeElement;
    const alertElement = container.querySelector('ngb-alert');
    if (!alertElement) {
      return;
    }

    const alertRect = alertElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const isInView = alertRect.bottom > 0 && alertRect.top < viewportHeight;
    if (isInView) {
      return;
    }

    alertElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  onlineTaskInspectionDone(): void {
    if (this.isSubmittingOnlineInspection) {
      return;
    }

    this.isSubmittingOnlineInspection = true;

    this.routeSubscription = this.route.params.subscribe((data) => {
      if(this.task.taskFiles.length === 0) {
        this.cantSendAlert = true;
        this.isSubmittingOnlineInspection = false;
        return;
      } else {
        this.cantSendAlert = false;
      }

      const promise = this.tasksService.onlineTaskInspectionDone(
        this.task.id,
        data.token
      );
      promise
        .then(() => {
          this.router.navigate(['online-task-inspection/thanks']);
        })
        .catch((err) => {
          this.logger.error('Done online inspection failed', err);
          alert('Nastala chyba při ukončování online prohlídky');
        })
        .finally(() => {
          this.isSubmittingOnlineInspection = false;
        });
    });
  }
}
